import React, {useCallback, useMemo} from 'react';
import {useFormikContext} from 'formik';
import excel from 'shared/Excel';
import {useProductContext} from 'contexts';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {PresetRow} from 'components/customer/Product/Drawer/PresetRow';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {
    buildPresets,
    drawerShape,
    shapesMatch,
    withCustom,
} from 'components/customer/Product/Drawer/helpers/drawerPresets';

interface DrawerPresetsProps {
    drawerParams: DrawerParameters;
}

// Applying a preset writes heights and auto flags straight to the form, so the
// runner lookups and validation run as if they were typed.
export const DrawerPresets = ({drawerParams}: DrawerPresetsProps) => {
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const {getMaterialOptions} = useProductContext<{
        getMaterialOptions: () => object;
    }>();
    const {resetRunners} = useDrawerDefaults();

    const faceHeightFieldName = drawerParams.drawerFaceHeightFieldName;
    const styleFieldName = drawerParams.drawerStyleFieldName;

    const presets = useMemo(() => {
        const drawers = values.drawers ?? [];

        if (drawers.length === 0) return [];

        const total = excel.calculate<number>(drawerParams.totalDrawerHeight, {
            ...values,
            ...getMaterialOptions(),
        });
        const margin = (fieldName: string) =>
            toNumber(values[String(fieldName)]);
        const space =
            total -
            margin(drawerParams.drawerTopMarginFieldName) -
            margin(drawerParams.drawerBottomMarginFieldName) -
            margin(drawerParams.drawerGapFieldName) * (drawers.length - 1);

        const current = drawerShape(
            drawers,
            faceHeightFieldName,
            styleFieldName
        );

        return withCustom(
            buildPresets(drawers.length, space),
            current,
            drawers.map((drawer) =>
                toNumber(drawer[String(faceHeightFieldName)])
            )
        ).map((preset) => ({
            ...preset,
            isActive: shapesMatch(preset.heights, current),
        }));
    }, [
        values,
        drawerParams,
        getMaterialOptions,
        faceHeightFieldName,
        styleFieldName,
    ]);

    const applyHandler = useCallback(
        (index: number) => {
            const preset = presets[Number(index)];

            if (!preset || preset.isCustom) return;

            void setTouched({drawers: true});

            preset.heights.forEach((height, position) => {
                if (height === null) {
                    void setFieldValue(
                        `drawers[${position}].${styleFieldName}`,
                        1
                    );
                    return;
                }

                void setFieldValue(
                    `drawers[${position}].${faceHeightFieldName}`,
                    height
                );
                void setFieldValue(`drawers[${position}].${styleFieldName}`, 0);
            });

            resetRunners();
        },
        [
            presets,
            setFieldValue,
            setTouched,
            faceHeightFieldName,
            styleFieldName,
            resetRunners,
        ]
    );

    return <PresetRow presets={presets} onApply={applyHandler} />;
};
